import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Info, Mail, MessageSquare } from 'lucide-react';
import { authApiV2 } from '@/api/endpoints/auth';
import { dashboardApi } from '@/api/endpoints/dashboard';
import { dashboardAccessContextQueryKey } from '@/features/dashboard/hooks/useDashboardAccessContext';
import { AuthTextField } from '@/features/auth/components/AuthTextField';
import { PrivacyPolicyDialog } from '@/features/auth/components/PrivacyPolicyDialog';
import { TermsOfServiceDialog } from '@/features/auth/components/TermsOfServiceDialog';
import { AuthMarketingShell } from '@/features/auth/components/AuthMarketingShell';
import { PreLogGuestRedirect } from '@/features/auth/components/PreLogGuestRedirect';
import {
  registerEmailSchema,
  registerProfileSchema,
  type RegisterEmailForm,
  type RegisterProfileForm,
} from '@/features/auth/lib/register-schema';
import { DEFAULT_POST_AUTH_PATH } from '@/lib/default-landing';
import { isDevAuth } from '@/lib/dev-auth';
import { getSupabaseClient, isSupabaseLoginEnabled } from '@/lib/supabase';
import { useAuthStore } from '@/stores/auth.store';
import { toast } from '@/stores/toast.store';
import { cn } from '@/lib/cn';
import { recoverRegisterCompleteAfterGatewayError } from '@/features/auth/lib/register-complete-recovery';
import { RESEND_COOLDOWN_SECONDS } from '@/features/auth/lib/resend-cooldown';
import { digitsOnly, formatPhone } from '@/lib/phone-format';

type VerificationChannel = 'sms' | 'email';

type PendingRegistration = {
  email: string;
  registrationToken: string;
  mfaChallengeToken: string;
  /** Stable Idempotency-Key reused across retries of the same OTP attempt (DEF-229). */
  completeIdempotencyKey: string;
  availableChannels: VerificationChannel[];
  codeSent: boolean;
  channel: VerificationChannel;
  phoneMasked: string | null;
  devSecret?: string;
};

/** Mask email for channel-picker copy (login MFA parity). */
function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!domain || !local) return email;
  if (local.length <= 2) return `${local[0] ?? ''}***@${domain}`;
  return `${local.slice(0, 2)}***@${domain}`;
}

function verifyErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const body = error.response?.data as
      | { error?: { code?: string; message?: string } }
      | undefined;
    const code = body?.error?.code;
    const message = body?.error?.message;
    if (code === 'mfa_resend_limited' || error.response?.status === 429) {
      return 'Too many codes requested. Please wait and try again.';
    }
    if (message) return message;
  }
  return 'Unable to send verification code. Please try again.';
}

/** Only `mfa_invalid` means a wrong OTP; other failures get retry-friendly copy (DEF-229). */
function completeErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const body = error.response?.data as
      | { error?: { code?: string; message?: string } }
      | undefined;
    const code = body?.error?.code;
    const message = body?.error?.message;
    const status = error.response?.status;
    if (code === 'mfa_invalid') {
      return message ?? "That didn't match. Please try again.";
    }
    if (code === 'mfa_challenge_invalid') {
      return message ?? 'Code expired. Request a new one.';
    }
    if (code === 'locked') {
      return message ?? 'Too many incorrect codes. Try again later.';
    }
    if (status != null && status >= 500) {
      return 'Something went wrong creating your account. Please try again.';
    }
    if (message) return message;
  }
  return 'Something went wrong creating your account. Please try again.';
}

const FORM_MODE = {
  mode: 'onTouched' as const,
  reValidateMode: 'onChange' as const,
};

const PROFILE_FIELDS = [
  ['firstName', 'First name', 'given-name', 'text', 50] as const,
  ['lastName', 'Last name', 'family-name', 'text', 50] as const,
  ['companyName', 'Company', 'organization', 'text', 255] as const,
  ['workPhone', 'Work phone', 'tel', 'tel', 50] as const,
  // `tel` (not "off") — Chrome otherwise treats the field before Password as username.
  ['authPhone', 'Mobile (for verification)', 'tel', 'tel', 50] as const,
  // `new-password` blocks filling a saved login password into Create Account.
  ['password', 'Password', 'new-password', 'password', undefined] as const,
];

/**
 * Invite-only registration: email gate → profile form → OTP → session.
 */
export function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { setTokens, setUser, isAuthenticated } = useAuthStore();
  const inviteEmailPrefill = (searchParams.get('email') ?? '').trim().toLowerCase();
  const [step, setStep] = useState<'email' | 'form' | 'verify'>('email');
  const [email, setEmail] = useState(inviteEmailPrefill);
  const [pending, setPending] = useState<PendingRegistration | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendConfirmation, setResendConfirmation] = useState<string | null>(null);

  const emailForm = useForm<RegisterEmailForm>({
    resolver: zodResolver(registerEmailSchema),
    defaultValues: { email: inviteEmailPrefill },
    ...FORM_MODE,
  });
  const gateEmailValue = emailForm.watch('email') ?? '';

  const registerForm = useForm<RegisterProfileForm>({
    resolver: zodResolver(registerProfileSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      workPhone: '',
      authPhone: '',
      password: '',
      termsAccepted: false,
    },
    ...FORM_MODE,
  });
  const profileValues = registerForm.watch();

  // Client-side resend cooldown countdown (DEF-233).
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = window.setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldown]);

  // Browser login autofill can still race past autocomplete=off and dump an
  // email into Mobile / a saved password into Password. Scrub after paint.
  useEffect(() => {
    if (step !== 'form') return;
    const scrubPhones = () => {
      const phone = registerForm.getValues('authPhone');
      const work = registerForm.getValues('workPhone');
      if (phone.includes('@')) {
        registerForm.setValue('authPhone', '', { shouldValidate: true });
      }
      if (work.includes('@')) {
        registerForm.setValue('workPhone', '', { shouldValidate: true });
      }
    };
    const scrubAutofillPassword = () => {
      // Form was just reset empty — a password in the first ~100ms is autofill.
      if (registerForm.getValues('password')) {
        registerForm.setValue('password', '', { shouldValidate: false });
      }
      scrubPhones();
    };
    const raf = window.requestAnimationFrame(scrubAutofillPassword);
    const t0 = window.setTimeout(scrubAutofillPassword, 50);
    const t1 = window.setTimeout(scrubPhones, 300);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t0);
      window.clearTimeout(t1);
    };
  }, [step, registerForm]);

  // Deep links carry a known email (invite emails, the login-side
  // registration_required reroute), so run the gate once automatically instead
  // of asking the user to re-submit an email they already typed. A failed
  // check falls back to the manual email step with the error shown.
  const autoCheckAttempted = useRef(false);
  useEffect(() => {
    if (autoCheckAttempted.current) return;
    if (step !== 'email' || pending != null) return;
    if (!registerEmailSchema.safeParse({ email: inviteEmailPrefill }).success) return;
    autoCheckAttempted.current = true;
    void onCheckEmail({ email: inviteEmailPrefill });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per prefill; onCheckEmail is stable in practice
  }, [step, pending, inviteEmailPrefill]);

  async function onCheckEmail(data: RegisterEmailForm) {
    setIsLoading(true);
    setError(null);
    try {
      const result = await authApiV2.checkEmail({ email: data.email });
      if (!result?.canRegister) {
        setError(
          result && 'message' in result && result.message
            ? result.message
            : 'It seems there\'s an issue with your account creation. Please contact your system administrator for further assistance or try again.',
        );
        return;
      }
      setEmail(result.email);
      registerForm.reset({
        firstName: '',
        lastName: '',
        companyName: '',
        workPhone: '',
        authPhone: '',
        password: '',
        termsAccepted: false,
      });
      setStep('form');
    } catch (err) {
      if (isAxiosError(err) && err.response == null) {
        setError(
          'Unable to verify your invitation right now. Check your connection and try again.',
        );
        return;
      }
      setError(
        'Unable to verify your invitation right now. Check your connection and try again.',
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function onRegister(data: RegisterProfileForm) {
    setIsLoading(true);
    setError(null);
    try {
      // DEF-293 — store digits; format is display-only on blur.
      const result = await authApiV2.register({
        email,
        ...data,
        workPhone: digitsOnly(data.workPhone) || data.workPhone,
        authPhone: digitsOnly(data.authPhone) || data.authPhone,
      });
      setPending({
        email: result.email,
        registrationToken: result.registrationToken,
        mfaChallengeToken: result.mfaChallengeToken,
        completeIdempotencyKey: crypto.randomUUID(),
        availableChannels: result.availableChannels,
        codeSent: result.codeSent,
        channel: result.channel ?? result.availableChannels[0] ?? 'email',
        phoneMasked: result.phoneMasked,
        devSecret: result.devSecret,
      });
      setStep('verify');
      if (result.codeSent) {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      }
    } catch (err) {
      if (isAxiosError(err)) {
        const message = (
          err.response?.data as { error?: { message?: string } } | undefined
        )?.error?.message;
        if (message) {
          setError(message);
          return;
        }
      }
      setError('Unable to continue registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  async function onSendCode(nextChannel: VerificationChannel) {
    if (!pending) return;
    setIsSendingCode(true);
    setError(null);
    setResendConfirmation(null);
    try {
      const result = await authApiV2.registerSendCode({
        registrationToken: pending.registrationToken,
        mfaChallengeToken: pending.mfaChallengeToken,
        channel: nextChannel,
      });
      setPending({
        ...pending,
        codeSent: true,
        channel: result.channel,
        phoneMasked: result.phoneMasked,
        availableChannels: result.availableChannels,
        mfaChallengeToken: result.mfaChallengeToken,
        // New OTP attempt — new idempotency key.
        completeIdempotencyKey: crypto.randomUUID(),
        devSecret: result.devSecret,
      });
      setCode('');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setResendConfirmation('A new code has been sent.');
    } catch (err) {
      setError(verifyErrorMessage(err));
    } finally {
      setIsSendingCode(false);
    }
  }

  async function onVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    if (!pending || code.length !== 6) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await authApiV2.registerComplete(
        {
          registrationToken: pending.registrationToken,
          mfaChallengeToken: pending.mfaChallengeToken,
          code,
        },
        { idempotencyKey: pending.completeIdempotencyKey },
      );

      // DEF-292 — land in-app when tokens are present. OTP already served as
      // the second factor, so this is not an MFA bypass. sessionPending (mint
      // failed) still falls through to sign-in.
      if (result.sessionPending || !result.accessToken || !result.refreshToken) {
        toast.success('Account created — please sign in.');
        navigate('/login', { replace: true });
        return;
      }

      setTokens({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
      });

      // Supabase client bridge is best-effort — API bearer tokens already set.
      if (isSupabaseLoginEnabled()) {
        await getSupabaseClient().auth.setSession({
          access_token: result.accessToken,
          refresh_token: result.refreshToken,
        });
      }

      try {
        const session = await authApiV2.me();
        setUser(session);
        queryClient.setQueryData(['auth', 'me'], session);
        queryClient.removeQueries({ queryKey: ['dashboard', 'access-context'] });
        await queryClient.fetchQuery({
          queryKey: dashboardAccessContextQueryKey(session.userId),
          queryFn: () => dashboardApi.getAccessContext(),
        });
        navigate(DEFAULT_POST_AUTH_PATH, { replace: true });
      } catch {
        // DEF-292 — me() failed but tokens are live; hydrate from complete payload.
        const fallback = {
          userId: result.user?.userId ?? 0,
          temporaryUsersId: null,
          username: result.user?.email ?? pending.email,
          email: result.user?.email ?? pending.email,
          token: result.accessToken,
          role: result.user?.role ?? '',
          roleId: 0,
          companyId: result.user?.companyId ?? 0,
          companyName: result.user?.companyName ?? '',
          firstName: result.user?.firstName ?? '',
          lastName: result.user?.lastName ?? '',
          name: `${result.user?.firstName ?? ''} ${result.user?.lastName ?? ''}`.trim(),
          isActiveDirectory: false,
          isSessionActivated: true,
          isApproved: true,
          active: true,
          authProvider: 'local' as const,
        };
        setUser(fallback);
        navigate(DEFAULT_POST_AUTH_PATH, { replace: true });
      }
    } catch (err) {
      // DEF-229 — gateway 502/503 after activation: confirm via check-email.
      if (await recoverRegisterCompleteAfterGatewayError(err, pending.email)) {
        toast.success('Account created — please sign in.');
        navigate('/login', { replace: true });
        return;
      }
      setError(completeErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }

  function handleTogglePasswordVisibility() {
    setShowPassword((visible) => !visible);
  }

  const showChannelPicker =
    pending != null && !pending.codeSent && pending.availableChannels.length > 1;
  const canSwitchChannel =
    pending != null && pending.codeSent && pending.availableChannels.length > 1;
  const otherChannel: VerificationChannel =
    pending?.channel === 'sms' ? 'email' : 'sms';
  const resendDisabled = isSendingCode || resendCooldown > 0;

  const shellTitle =
    step === 'email'
      ? 'Set up your account'
      : step === 'form'
        ? 'Your profile'
        : 'Verify your identity';
  const shellSubtitle =
    step === 'email'
      ? 'Enter the work email from your invitation.'
      : step === 'form'
        ? 'A few details so we can finish creating your account.'
        : 'Confirm it’s you with a one-time code.';

  if (isAuthenticated && !isDevAuth() && step === 'email' && pending == null) {
    return <PreLogGuestRedirect />;
  }

  return (
    <AuthMarketingShell
      title={shellTitle}
      subtitle={shellSubtitle}
      footer={
        <Link to="/login" className="font-medium text-primary hover:text-primary hover:underline">
          Back to sign in
        </Link>
      }
    >
      <TermsOfServiceDialog open={termsOpen} onOpenChange={setTermsOpen} />
      <PrivacyPolicyDialog open={privacyOpen} onOpenChange={setPrivacyOpen} />

        {error && (
          <div className="mb-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive-text">
            {error}
          </div>
        )}

        {step === 'email' && (
          <form
            onSubmit={emailForm.handleSubmit(onCheckEmail)}
            className="flex flex-col gap-4"
            autoComplete="off"
            noValidate
          >
            <div>
              <label htmlFor="reg-email" className="mb-1.5 block text-sm font-medium text-foreground">
                Work email
              </label>
              <AuthTextField
                {...emailForm.register('email')}
                id="reg-email"
                type="email"
                autoComplete="off"
                value={gateEmailValue}
                onClear={() =>
                  emailForm.setValue('email', '', {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                clearLabel="Clear email"
                isInvalid={!!emailForm.formState.errors.email}
                placeholder="you@company.com"
              />
              {emailForm.formState.errors.email && (
                <p className="mt-1 text-xs text-destructive-text">
                  {emailForm.formState.errors.email.message}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {isLoading ? 'Checking…' : 'Continue'}
            </button>
          </form>
        )}

        {step === 'form' && (
          <form
            onSubmit={registerForm.handleSubmit(onRegister)}
            className="relative flex flex-col gap-4"
            autoComplete="off"
            noValidate
          >
            {/*
              Chrome ignores form autocomplete=off near a password field and
              dumps the saved login into Mobile + Password. These sinks absorb
              that pass before the real Create Account fields.
            */}
            <div
              className="pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0"
              aria-hidden="true"
            >
              <input type="text" name="username" tabIndex={-1} autoComplete="username" />
              <input
                type="password"
                name="current-password"
                tabIndex={-1}
                autoComplete="current-password"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Registering as <span className="text-foreground">{email}</span>
            </p>
            {PROFILE_FIELDS.map(([name, label, autoComplete, inputType, maxLength]) => {
              const fieldValue = String(profileValues[name] ?? '');
              const isPassword = name === 'password';
              const fieldRegister = registerForm.register(name);
              const isPhoneField = name === 'workPhone' || name === 'authPhone';
              return (
                <div key={name}>
                  <label
                    htmlFor={`reg-${name}`}
                    className="mb-1.5 block text-sm font-medium text-foreground"
                  >
                    {label}
                  </label>
                  <AuthTextField
                    {...fieldRegister}
                    id={`reg-${name}`}
                    type={isPassword ? 'password' : inputType}
                    autoComplete={autoComplete}
                    maxLength={maxLength}
                    value={fieldValue}
                    onClear={() =>
                      registerForm.setValue(name, '', {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                    clearLabel={`Clear ${label.toLowerCase()}`}
                    passwordToggle={isPassword}
                    isPasswordVisible={isPassword ? showPassword : false}
                    onTogglePasswordVisibility={
                      isPassword ? handleTogglePasswordVisibility : undefined
                    }
                    isInvalid={!!registerForm.formState.errors[name]}
                    aria-describedby={
                      name === 'authPhone' ? 'reg-authPhone-help' : undefined
                    }
                    onBlur={(event) => {
                      void fieldRegister.onBlur(event);
                      if (isPhoneField) {
                        // DEF-293 — format for display; digits stored on submit.
                        registerForm.setValue(
                          name,
                          formatPhone(registerForm.getValues(name)),
                          { shouldDirty: true, shouldValidate: true },
                        );
                      }
                    }}
                  />
                  {name === 'authPhone' && (
                    <p
                      id="reg-authPhone-help"
                      className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground"
                    >
                      <Info
                        className="mt-0.5 h-3.5 w-3.5 shrink-0"
                        aria-hidden="true"
                      />
                      <span>Used only for two-factor authentication codes.</span>
                    </p>
                  )}
                  {name === 'password' && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      At least 8 characters with uppercase, lowercase, and a digit.
                    </p>
                  )}
                  {registerForm.formState.errors[name] && (
                    <p className="mt-1 text-xs text-destructive-text">
                      {registerForm.formState.errors[name]?.message}
                    </p>
                  )}
                </div>
              );
            })}
            <div>
              <label className="flex items-start gap-2 text-xs text-muted-foreground">
                <input
                  {...registerForm.register('termsAccepted')}
                  type="checkbox"
                  aria-invalid={!!registerForm.formState.errors.termsAccepted}
                  className="mt-0.5 h-4 w-4 rounded border-input"
                />
                <span>
                  I accept the{' '}
                  <button
                    type="button"
                    onClick={() => setTermsOpen(true)}
                    className="text-foreground underline underline-offset-2 hover:text-primary"
                  >
                    Terms of Service
                  </button>{' '}
                  and{' '}
                  <button
                    type="button"
                    onClick={() => setPrivacyOpen(true)}
                    className="text-foreground underline underline-offset-2 hover:text-primary"
                  >
                    Privacy Policy
                  </button>
                </span>
              </label>
              {registerForm.formState.errors.termsAccepted && (
                <p className="mt-1 text-xs text-destructive-text">
                  {registerForm.formState.errors.termsAccepted.message}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {isLoading ? 'Sending code…' : 'Continue'}
            </button>
          </form>
        )}

        {step === 'verify' && pending && showChannelPicker && (
          <div className="flex flex-col gap-4">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-foreground">Verify your identity</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Choose how you&apos;d like to receive your verification code.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              {pending.availableChannels.includes('sms') && (
                <button
                  type="button"
                  disabled={isSendingCode}
                  onClick={() => onSendCode('sms')}
                  className={cn(
                    'flex items-center gap-4 rounded-lg border border-input bg-background px-4 py-4 text-left transition-colors hover:bg-accent hover:text-accent-foreground',
                    'disabled:opacity-50',
                  )}
                >
                  <MessageSquare className="h-6 w-6 shrink-0 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-foreground">Text message</p>
                    <p className="text-xs text-muted-foreground">
                      Send to {pending.phoneMasked || 'your phone'}
                    </p>
                  </div>
                </button>
              )}
              {pending.availableChannels.includes('email') && (
                <button
                  type="button"
                  disabled={isSendingCode}
                  onClick={() => onSendCode('email')}
                  className={cn(
                    'flex items-center gap-4 rounded-lg border border-input bg-background px-4 py-4 text-left transition-colors hover:bg-accent hover:text-accent-foreground',
                    'disabled:opacity-50',
                  )}
                >
                  <Mail className="h-6 w-6 shrink-0 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-foreground">Email</p>
                    <p className="text-xs text-muted-foreground">
                      Send to {maskEmail(pending.email)}
                    </p>
                  </div>
                </button>
              )}
            </div>

            {isSendingCode && (
              <p className="text-center text-xs text-muted-foreground">Sending code…</p>
            )}
          </div>
        )}

        {step === 'verify' && pending && !showChannelPicker && (
          <form
            onSubmit={onVerifyOtp}
            className="flex flex-col gap-4"
            autoComplete="off"
            noValidate
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted-foreground/20">
                {pending.channel === 'sms' ? (
                  <MessageSquare className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <Mail className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Enter verification code</h2>
                <p className="text-xs text-muted-foreground">
                  {pending.channel === 'sms'
                    ? `Sent to ${pending.phoneMasked || 'your phone'}`
                    : `Sent to ${maskEmail(pending.email)}`}
                </p>
              </div>
            </div>

            {resendConfirmation && (
              <p className="text-xs text-primary" role="status" aria-live="polite">
                {resendConfirmation}
              </p>
            )}

            {import.meta.env.DEV && pending.devSecret && (
              <p className="rounded-md bg-warning-bg px-3 py-2 text-xs text-warning">
                Local dev helper: your code is{' '}
                <span className="font-mono font-semibold tracking-wider">
                  {pending.devSecret}
                </span>
                . Check your inbox too — email may also have been sent.
              </p>
            )}

            <AuthTextField
              name="one-time-code"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onClear={() => setCode('')}
              clearLabel="Clear verification code"
              inputClassName="text-center font-mono text-lg tracking-[0.5em]"
              autoFocus
              autoComplete="one-time-code"
              aria-label="6-digit verification code"
            />

            <div className="flex flex-col items-center gap-2 text-xs text-muted-foreground">
              <button
                type="button"
                disabled={resendDisabled}
                onClick={() => onSendCode(pending.channel)}
                className="underline underline-offset-2 hover:text-foreground disabled:opacity-50"
              >
                {isSendingCode
                  ? 'Sending…'
                  : resendCooldown > 0
                    ? `Resend code (${resendCooldown}s)`
                    : 'Resend code'}
              </button>
              {canSwitchChannel && (
                <button
                  type="button"
                  disabled={resendDisabled}
                  onClick={() => onSendCode(otherChannel)}
                  className="underline underline-offset-2 hover:text-foreground disabled:opacity-50"
                >
                  Send code via {otherChannel === 'sms' ? 'text message' : 'email'} instead
                </button>
              )}
            </div>

            <button
              type="submit"
              disabled={code.length !== 6 || isLoading}
              className="rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {isLoading ? 'Creating account…' : 'Verify and finish'}
            </button>
          </form>
        )}

    </AuthMarketingShell>
  );
}
